import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, FileText, Image, File, AlertCircle } from "lucide-react";

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "docx", "xlsx"];

function getFileIcon(name) {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png"].includes(ext)) return <Image className="w-4 h-4 text-blue-500" />;
  if (ext === "pdf") return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-slate-400" />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUploadZone({ files, setFiles, requirement = "optional", disabled = false }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (newFiles) => {
    const fileList = Array.from(newFiles);
    const errors = [];
    const validFiles = [];

    for (const f of fileList) {
      if (files.length + validFiles.length >= MAX_FILES) {
        errors.push(`Maximo ${MAX_FILES} archivos por solicitud`);
        break;
      }
      const ext = f.name.split(".").pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errors.push(`${f.name}: tipo no permitido (${ALLOWED_EXTENSIONS.join(", ")})`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        errors.push(`${f.name}: demasiado grande (max 15MB)`);
        continue;
      }
      if (files.some(ef => ef.name === f.name && ef.size === f.size)) {
        continue; // duplicate
      }
      validFiles.push(f);
    }
    if (validFiles.length > 0) setFiles([...files, ...validFiles]);
    return errors;
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!disabled) addFiles(e.dataTransfer.files);
  };

  const reqLabel = requirement === "required" ? "Obligatorio" : requirement === "recommended" ? "Recomendado" : "Opcional";
  const reqColor = requirement === "required" ? "text-red-600 bg-red-50 border-red-200" : requirement === "recommended" ? "text-amber-600 bg-amber-50 border-amber-200" : "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <div className="space-y-2" data-testid="file-upload-zone">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Documentos adjuntos</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-semibold ${reqColor}`} data-testid="doc-requirement-badge">
          {reqLabel}
        </span>
      </div>

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-sm p-4 text-center transition-colors cursor-pointer
          ${dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-slate-50/50"}
          ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        data-testid="file-drop-area"
      >
        <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1.5" />
        <p className="text-[11px] text-slate-500">Arrastra archivos aqui o <span className="text-blue-600 font-medium">haz clic para seleccionar</span></p>
        <p className="text-[9px] text-slate-400 mt-1">PDF, JPG, PNG, DOCX, XLSX - Max 15MB - Hasta {MAX_FILES} archivos</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          data-testid="file-input"
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1" data-testid="file-list">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-slate-200 rounded-sm" data-testid={`file-item-${i}`}>
              {getFileIcon(f.name)}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-slate-800 truncate">{f.name}</p>
                <p className="text-[9px] text-slate-400">{formatSize(f.size)}</p>
              </div>
              {!disabled && (
                <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-file-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Requirement warning */}
      {requirement === "required" && files.length === 0 && (
        <div className="flex items-center gap-1.5 text-[10px] text-amber-600" data-testid="doc-warning">
          <AlertCircle className="w-3 h-3" />
          Sin documentos adjuntos. La solicitud se creara como &quot;Documentacion pendiente&quot;.
        </div>
      )}
    </div>
  );
}
